import React, { useState, useEffect } from 'react';
import './styles.css';
import PerformanceTable from '../../../components/performanceTable';
import SubjectHeader from '../../../components/subjectHeader';
import { Colors } from '../../../utils/constants/colors';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { PieChart, Pie, Cell } from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiCollection } from '../../../config/envConfig';
import { useAxios } from '../../../hooks/useAxios';
import { AnalysisResponse, GaugeChartProps } from '../../../utils/interface/examAnalysis';
import SpinnerLoader from '../../../components/loader';
import AnalysisPoster from '../../../assets/analysisPoster.svg'
import { intervalToDuration } from 'date-fns'

const GaugeChart = ({ correct, incorrect, notAttempted, total }: GaugeChartProps) => {
  const data = [
    { name: 'Obtained', value: correct, color: '#4CAF50' },
    { name: 'Lost', value: incorrect, color: '#FF5252' },
    { name: 'Not Attempted', value: notAttempted, color: '#2196F3' },
  ];

  return (
    <div className="gauge_container">
      <div className="donut-chart">
        <PieChart width={400} height={300}>
          <Pie
            data={data}
            cx={200}
            cy={150}
            innerRadius={80}
            outerRadius={120}
            paddingAngle={3}
            dataKey="value"
            startAngle={90}
            endAngle={450}
          // labelLine={true}
          // label={renderCustomizedLabel}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <text
            x={200}
            y={140}
            textAnchor="middle"
            dominantBaseline="central"
            className="donut-center-text"
          >
            {total}
          </text>
          <text
            x={200}
            y={165}
            textAnchor="middle"
            dominantBaseline="central"
            className="donut-subtext"
          >
            Total Marks
          </text>
        </PieChart>
      </div>
    </div>
  );
};

const TestAnalysisScreen = () => {
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('examId');
  const title = searchParams.get('title');
  const subjectId = searchParams.get('subjectId');

  const axios = useAxios();
  const navigate = useNavigate();


  const [analysisData, setAnalysisData] = useState<AnalysisResponse | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);



  useEffect(() => {
    fetchAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchAnalysis = async () => {
    setLoading(true);
    await axios.get(`${ApiCollection.subject.getAnalysis}?exam_id=${examId}`)
      .then((response) => {
        setAnalysisData(response.data || null);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setAnalysisData(null)
        setLoading(false);
      });
  };


  const handlePagnination = (type: 'prev' | 'next') => {

    if (type === 'prev') {
      if (currentPage === 1) return;
      setCurrentPage(currentPage - 1);
    } else {
      if (currentPage === getSummaryData()?.totalPages) return;
      setCurrentPage(currentPage + 1);
    }
  }


  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '100%', width: '100%' }}>
        <SpinnerLoader />
        <h2 style={{ marginTop: 10, fontSize: 20 }}>
          Loading...
        </h2>
      </div>
    )

  }


  const getSummaryData = () => {

    if (!analysisData) return;

    const data = analysisData.topic_summary.map((topic) => ({
      topic: topic.topic_name,
      achieved: topic.marks_obtained,
      total: topic.total_marks,
      swot: topic.swot,
      attempt: Math.round((topic.question_attempted / topic.total_questions) * 100)
    }));

    const rowsPerPage = 5;
    const totalPages = Math.ceil(data.length / rowsPerPage);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

    return {
      currentRows,
      totalPages,
      data
    }
  }

  const getColor = (aquired: number, total: number) => {
    const percentage = (aquired / total) * 100;

    if (percentage >= 80) return Colors.success;
    if (percentage >= 50) return Colors.warning;
    return Colors.error;
  }



  return (
    analysisData ?
      <div className="student_analysis_screen">
        <div className='analysis_wrapper'>
          <SubjectHeader
            title={'Analysis'}
            className='analysis_header'
            description='Get a detailed analysis of your performance in the last test'
            right={
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => navigate(`/test/grading?examId=${examId}&title=${title}`)}
                  className='primary_btn view_grading_on_analysis'>View Grading</button>
              </div>
            }
          />

          <div className='overview_container'>
            <h3 className="topic_summary_title overview">Overview</h3>
            <div className='test_meta_holder'>
              <div className='test_meta'>
                <span className='test_meta_title'>Marks Obtained</span>
                <div className='test_meta_data'>
                  <span style={{
                    fontWeight: 'bold',
                    color: getColor(analysisData.marks_obtained, analysisData.total_marks)
                  }}>{analysisData.marks_obtained}</span>

                  <span style={{ fontSize: 18, color: 'black' }}>/ {analysisData.total_marks}</span>
                </div>
              </div>


              <div className='test_meta'>
                <span className='test_meta_title'>Question Attempted</span>
                <div className='test_meta_data'>
                  <span style={{
                    fontWeight: 'bold',
                    color: getColor(analysisData.question_attempted, analysisData.total_questions)
                  }}>{analysisData.question_attempted}</span>
                  <span style={{ fontSize: 18, color: 'black' }}>/ {analysisData.total_questions}</span>
                </div>
              </div>

              <div className='test_meta'>
                <span className='test_meta_title'>Time Taken</span>
                <div className='test_meta_data'>
                  <span style={{
                    fontWeight: 'bold',
                    color: 'black'
                  }}>{
                      analysisData.total_time ?
                        intervalToDuration({ start: 0, end: analysisData.total_time * 1000 }).minutes
                        :
                        0
                    }</span>
                  <span style={{ fontSize: 18, color: 'black' }}> mins</span>
                </div>
              </div>

              <div className='test_meta'>
                <span className='test_meta_title'>Performance</span>
                {
                  analysisData.performance == null ?
                    <span style={{ fontSize: 16, color: 'gray' }}>
                      Take another test to check your improvements !
                    </span>
                    :
                    <div className='test_meta_data'>
                      <span style={{
                        fontWeight: 'bold',
                        color: analysisData.performance.isImproved ? Colors.success : Colors.error
                      }}>{analysisData.performance.percentage}%</span>
                      <span style={{ fontSize: 16, color: 'black' }}> improvement</span>
                    </div>
                }

              </div>


            </div>

            <div className='test_meta summary'>
              <span className='test_meta_title'>Exam Summary</span>
              <div className='test_meta_data summary'>
                <span>{analysisData.exam_summary}</span>
              </div>
            </div>

          </div>

          <div className='chart_container'>
            <div className="topic_summary">
              <div className="topic_summary_header">
                <h3 className="topic_summary_title">Topic Summary</h3>
                {
                  (currentPage === getSummaryData()?.totalPages && currentPage === 1) === false &&
                  <div className="topic_summary_pagination">
                    <div className={`topic_summary_pagination_button ${currentPage === 1 ? 'disabled' : ''} prev`} onClick={() => handlePagnination('prev')}>
                      <IoIosArrowBack size={22} />
                    </div>
                    <div style={{ margin: '0 10px', fontWeight: 'bold' }}>
                      <span>{currentPage}</span>
                    </div>
                    <div className={`topic_summary_pagination_button ${currentPage === getSummaryData()?.totalPages ? 'disabled' : ''} next`} onClick={() => handlePagnination('next')}>
                      <IoIosArrowForward size={22} />
                    </div>
                  </div>
                }

              </div>

              <PerformanceTable data={getSummaryData()?.currentRows} />
            </div>

            <div className="attempt_progress">
              <h3 className="topic_summary_title gauage">Marks Analysis</h3>
              <GaugeChart
                correct={analysisData.pieChart.marks_obtained.value}
                incorrect={analysisData.pieChart.marks_lost.value}
                notAttempted={analysisData.pieChart.marks_not_attempted.value}
                total={analysisData.total_marks}
              />
              <div className="attempt_progress_legend">
                <div className="legend_item">
                  <span className="legend_text" style={{ color: '#4CAF50' }}>{analysisData.pieChart.marks_obtained.value}</span>
                  <span className='legend_label'>Obtained</span>
                </div>

                <div className="legend_item">
                  <span className="legend_text" style={{ color: '#2196F3' }}>{analysisData.pieChart.marks_not_attempted.value}</span>
                  <span className='legend_label'>Not Attempted</span>
                </div>

                <div className="legend_item">
                  <span className="legend_text" style={{ color: '#FF5252' }}>{analysisData.pieChart.marks_lost.value}</span>
                  <span className='legend_label'>Lost</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      :
      <div className="student_analysis_screen analysis_in_process">
        <SubjectHeader title={''} description='' />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '90%', width: '100%' }}>

          <img
            src={AnalysisPoster}
            alt="analysis poster"
            style={{ width: 200, height: 200, objectFit: 'contain' }}
          />

          <h2 style={{ marginTop: 10, fontSize: 20 }}>Analysis in process !</h2>
          <span
            className='analysis_in_process_text'
            style={{ marginTop: 10, fontSize: 16, color: 'gray', width: '60%', textAlign: 'center' }}
          >Hey there! Our AI is busy grading and analyzing your answers. Hang tight and check back soon for your marks and detailed feedback. Keep rocking your studies!</span>

          <span>
            <button
              onClick={() => navigate(`/subject/view?subjectId=${subjectId}`)}
              className='primary_btn'
              style={{ marginTop: 30, transform: 'scale(0.9)' }}
            >Go Back</button>
          </span>
        </div>
      </div>
  );
};

export default TestAnalysisScreen;
